import React, { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Modal,
  IconButton,
  Switch,
  Chip,
  styled,
  alpha,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "../../assets/images2/deleteIcon.svg";
import { fetchAdvertisementsData } from "../../action/advertisementsActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import ClearIcon from "@mui/icons-material/Clear";
import CustomDeleteModal from "../../component/Modals/CustomDeleteModal";
import BannerSkeleton from "../../component/skeleton/BannerSkeleton";
import EditIcon from "@mui/icons-material/Edit";
import Pagination from "../../component/Pagination";
import { sortAdvertisementsByDate } from "../../utils/advertisementSort";

const columns = [
  { id: "banner", label: "BANNER", width: "18%" },
  { id: "link", label: "LINK", width: "42%" },
  { id: "status", label: "STATUS", width: "15%" },
  { id: "actions", label: "ACTIONS", width: "25%" },
];

const RedSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#ef364e",
    "&:hover": {
      backgroundColor: alpha("#ef364e", theme.palette.action.hoverOpacity),
    },
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#ef364e",
  },
}));

function truncateLink(url, max = 48) {
  const text = String(url || "");
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function AdvertisementStatusSwitch({ row, token, onUpdated }) {
  const [checked, setChecked] = useState(Boolean(Number(row.is_enabled)));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setChecked(Boolean(Number(row.is_enabled)));
  }, [row.is_enabled]);

  const handleChange = async (e) => {
    const next = e.target.checked;
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateAdvertisementStatus`,
        { id: row.id, is_enabled: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setChecked(next);
        toast.success(response.data.message);
        onUpdated();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Status update failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress size={22} sx={{ color: "#ef364e" }} />;
  }

  return <RedSwitch checked={checked} onChange={handleChange} />;
}

const Advertisements = () => {
  const navigate = useNavigate();
  const adsData = useSelector((state) => state.advertisements);
  const token = useSelector((state) => state.auth.token);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const [viewBanner, setViewBanner] = useState(false);
  const currentRef = useRef(null);
  const currentCallback = useRef(null);
  const currentTextRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    dispatch(fetchAdvertisementsData(token, 1));
  }, [dispatch, token]);

  useEffect(() => {
    if (adsData?.data?.success) {
      if (adsData?.data?.data?.rows?.length > 0) {
        setRows(sortAdvertisementsByDate(adsData.data.data.rows));
        setCurrentPage(adsData.data.data.currentPage || 1);
        setTotalPages(
          adsData.data.data.totalPages ||
            Math.ceil((adsData.data.data.count || 0) / 10)
        );
      } else {
        setRows("NO Data");
      }
    } else if (adsData?.data && !adsData?.data?.success) {
      setRows(adsData.data.message);
    }
  }, [adsData]);

  const handleChangePage = (_, newPage) => {
    dispatch(fetchAdvertisementsData(token, newPage));
  };

  const refreshList = () => {
    dispatch(fetchAdvertisementsData(token, currentPage));
  };

  const handleDelete = async (ref) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteAdvertisement/${ref.current.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        dispatch(fetchAdvertisementsData(token, currentPage));
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    } finally {
      setOpenDelete(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Box className="page_title custom-header">
        <Box>
          <Typography variant="h1" component="h1">
            Advertisements
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 0.5 }}>
            Manage competition listing carousel banners
          </Typography>
        </Box>
        <Box className="create-button-wrapper">
          <Button
            className="create-custom"
            onClick={() => navigate("/admin/dashboard/advertisements/add")}
          >
            <AddIcon className="delete-icon" /> Create
          </Button>
        </Box>
      </Box>

      <Box className="page_inner">
        <Box className="page_table tournament">
          <TableContainer
            component={Paper}
            className="customer_form user_table"
          >
            <Table
              aria-label="advertisements table"
              className="display responsive nowrap"
              style={{ width: "100%", minWidth: "700px" }}
            >
              <TableHead className="table_Head">
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sx={{ width: column.width }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(rows) && rows.length > 0 ? (
                  rows.map((value) => (
                    <TableRow key={value.id}>
                      <TableCell>
                        <Box className="property_name">
                          <Box className="bannerImageOuter">
                            <img src={value.banner_image} alt="advertisement" />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {value.link ? (
                          <Typography
                            component="a"
                            href={value.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="table-text"
                            sx={{
                              color: "#2563eb",
                              textDecoration: "none",
                              wordBreak: "break-all",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {truncateLink(value.link)}
                          </Typography>
                        ) : (
                          <Typography
                            className="table-text"
                            sx={{ color: "#9ca3af", fontStyle: "italic" }}
                          >
                            No link
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <AdvertisementStatusSwitch
                            row={value}
                            token={token}
                            onUpdated={refreshList}
                          />
                          <Chip
                            size="small"
                            label={Number(value.is_enabled) ? "Enabled" : "Disabled"}
                            color={Number(value.is_enabled) ? "success" : "default"}
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box className="table-actions">
                          <Box className="action-wrapper">
                            <Button
                              variant="text"
                              onClick={() => {
                                previewRef.current = value;
                                currentRef.current = value;
                                currentCallback.current = handleDelete;
                                currentTextRef.current =
                                  "Are you sure you want to delete this advertisement?";
                                setOpenDelete(true);
                              }}
                              sx={{ paddingRight: "0px", minWidth: "0px" }}
                            >
                              <img src={DeleteIcon} alt="" />
                            </Button>
                            <Button
                              variant="outlined"
                              className="visible_user"
                              onClick={() => {
                                previewRef.current = value;
                                setViewBanner(true);
                              }}
                            >
                              <VisibilityIcon className="visible_icon" />
                            </Button>
                            <Button
                              variant="outlined"
                              className="visible_user"
                              onClick={() =>
                                navigate(
                                  `/admin/dashboard/advertisements/edit/${value.id}`
                                )
                              }
                            >
                              <EditIcon className="visible_icon" />
                            </Button>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : rows !== "NO Data" ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <BannerSkeleton key={i} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} style={{ textAlign: "center" }}>
                      {rows}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handleChangePage={handleChangePage}
            />
          )}
        </Box>
      </Box>

      <Modal open={viewBanner} onClose={() => setViewBanner(false)}>
        <Box className="viewBannerModal">
          <Box className="bannerModalImageOuter">
            {previewRef.current && (
              <img src={previewRef.current.banner_image} alt="advertisement" />
            )}
          </Box>
          <IconButton
            className="clear-button"
            onClick={() => setViewBanner(false)}
          >
            <ClearIcon />
          </IconButton>
        </Box>
      </Modal>

      <CustomDeleteModal
        open={openDelete}
        setOpen={setOpenDelete}
        selected={currentRef}
        deleteCallBack={currentCallback}
        customText={currentTextRef.current}
      />
    </>
  );
};

export default Advertisements;
